import { createRoot, hydrateRoot, type Root } from 'react-dom/client';
import type { OnRenderClientAsync } from 'vike/types';

import { Providers } from './Providers';

import '../styles/global.css';

let root: Root | undefined;

/**
 * Client rendering.
 *
 * The first render hydrates the server HTML; every navigation after that
 * re-renders into the same root. Creating a new root per navigation would
 * discard and rebuild the whole tree, which is slower and loses focus position.
 *
 * A client-only page — the admin group — has no server HTML to hydrate and
 * takes the `createRoot` path.
 */
const onRenderClient: OnRenderClientAsync = async (
  pageContext,
): ReturnType<OnRenderClientAsync> => {
  const { Page, isHydration } = pageContext;

  if (!Page) {
    throw new Error('onRenderClient was called without a Page component');
  }

  const container = document.getElementById('root');
  if (!container) {
    throw new Error('#root is missing from the document');
  }

  const tree = (
    <Providers pageContext={pageContext}>
      <Page />
    </Providers>
  );

  if (isHydration && container.innerHTML !== '') {
    root = hydrateRoot(container, tree);
    return;
  }

  if (!root) {
    root = createRoot(container);
  }
  root.render(tree);
};

export default onRenderClient;
